import { z } from 'zod';

/**
 * The AI may operate everything the user sees. Four categories, three of them
 * exceptions; every element outside them is operated freely (category 1):
 * asking first (2), reading only (3) and secret content (4). The client maps
 * areas and functions of the UI to them in one central list; single elements
 * never declare them.
 */

/** Category 1: operating any other visible element. */
export const UI_OPERATE_CAPABILITY = 'ui.operate';

/**
 * Deleting by operating the app: a control that takes something away carries
 * the delete capability of what it removes, the one its tool carries too.
 */
export const UI_DELETE_CAPABILITIES = [
  'document.delete',
  'database.delete',
  'app_base.delete',
] as const;

/** Category 2: the AI acts only after the user confirms (destructive or crossing a security boundary). */
export const UI_CONFIRM_CAPABILITIES = [
  'people.invite',
  'people.manage',
  'document.share',
  'app_base.access',
  'mail.send',
  'ui.destructive',
  ...UI_DELETE_CAPABILITIES,
] as const;

/** Category 3: the AI may read and open these places, never act in them. */
export const UI_READ_ONLY_CAPABILITIES = [
  'workspace.delete',
  'account.delete',
  'data.purge',
  'history.restore',
] as const;

export type UiConfirmCapability = (typeof UI_CONFIRM_CAPABILITIES)[number];
export type UiReadOnlyCapability = (typeof UI_READ_ONLY_CAPABILITIES)[number];

/**
 * Category 4: the element keeps its place and its label, its content never
 * leaves the browser. The AI neither reads nor writes it; the user fills it in.
 */
export const UI_SECRET_ACCESS = { level: 'secret' } as const;

/** An element's exception; absent for category 1. */
export type UiElementAccess =
  | { level: 'confirm'; capability: UiConfirmCapability }
  | { level: 'read'; capability: UiReadOnlyCapability }
  | typeof UI_SECRET_ACCESS;

/** Whether two reads classify an element the same. */
export function isSameUiElementAccess(
  a: UiElementAccess | undefined,
  b: UiElementAccess | undefined
): boolean {
  if (!a || !b) return a === b;
  if (a.level === 'secret' || b.level === 'secret') return a.level === b.level;
  return a.level === b.level && a.capability === b.capability;
}

export function isUiConfirmCapability(
  value: string
): value is UiConfirmCapability {
  return (UI_CONFIRM_CAPABILITIES as readonly string[]).includes(value);
}

export function isUiReadOnlyCapability(
  value: string
): value is UiReadOnlyCapability {
  return (UI_READ_ONLY_CAPABILITIES as readonly string[]).includes(value);
}

/**
 * Category 2 among all assistant capabilities: UI exceptions that ask plus
 * every delete. The assistant asks before each such action until a workspace
 * admin loosens the capability; category 3 can never be released.
 */
export function isConfirmBoundAssistantCapability(value: string): boolean {
  return isUiConfirmCapability(value) || value.endsWith('.delete');
}

export const uiElementAccessSchema: z.ZodType<UiElementAccess> =
  z.discriminatedUnion('level', [
    z
      .object({
        level: z.literal('confirm'),
        capability: z.enum(UI_CONFIRM_CAPABILITIES),
      })
      .strict(),
    z
      .object({
        level: z.literal('read'),
        capability: z.enum(UI_READ_ONLY_CAPABILITIES),
      })
      .strict(),
    z.object({ level: z.literal('secret') }).strict(),
  ]);
